import Editor from '@monaco-editor/react';
import {
	AlertCircle,
	ChevronDown,
	Eye,
	FileText,
	HelpCircle,
	Loader2,
	Lock,
	Maximize2,
	Minimize2,
	Plus,
	Settings,
	Unlock,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useIsDark } from '../../contexts/ThemeContext';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation } from '../../hooks/useNodeValidation';
import type { PdfNodeConfig, ProcessingNodeData } from '../../types/nodes';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';

interface PdfConfigProps {
	nodeId: string;
	data: ProcessingNodeData;
	onChange: (data: ProcessingNodeData) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
	onClose?: () => void;
}

const htmlTemplates = [
	{
		name: 'Basic Document',
		template: `<html>
<head>
    <title>Generated Document</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        h1 { color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px; }
        .section { margin: 20px 0; }
        .highlight { background-color: #f8f9fa; padding: 10px; border-left: 4px solid #007bff; }
    </style>
</head>
<body>
    <h1>Document Title</h1>
    <div class="section">
        <p>Generated on: \$\{execution.startTime\}</p>
        <p>Execution ID: \$\{execution.id\}</p>
    </div>
    <div class="highlight">
        <p>Add your content here using tokens like \$\{data.fieldName\}</p>
    </div>
</body>
</html>`
			.replace(/\\\$\\\{/g, '{{')
			.replace(/\\\}/g, '}}'),
	},
	{
		name: 'Invoice Template',
		template: `<html>
<head>
    <title>Invoice</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        .header { text-align: center; border-bottom: 2px solid #007bff; padding-bottom: 20px; margin-bottom: 30px; }
        .invoice-details { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .invoice-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        .invoice-table th, .invoice-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
        .invoice-table th { background-color: #f8f9fa; }
        .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
    </style>
</head>
<body>
    <div class="header">
        <h1>INVOICE</h1>
        <p>Invoice #\$\{invoice.number\}</p>
    </div>

    <div class="invoice-details">
        <div>
            <h3>Bill To:</h3>
            <p>\$\{customer.name\}</p>
            <p>\$\{customer.email\}</p>
        </div>
        <div>
            <p><strong>Date:</strong> \$\{execution.startTime\}</p>
            <p><strong>Due Date:</strong> \$\{invoice.dueDate\}</p>
        </div>
    </div>

    <table class="invoice-table">
        <thead>
            <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>\$\{item.description\}</td>
                <td>\$\{item.quantity\}</td>
                <td>\$\{item.price\}</td>
                <td>\$\{item.total\}</td>
            </tr>
        </tbody>
    </table>

    <div class="total">
        Total: $\$\{invoice.total\}
    </div>
</body>
</html>`
			.replace(/\\\$\\\{/g, '{{')
			.replace(/\\\}/g, '}}'),
	},
	{
		name: 'Report Template',
		template: `<html>
<head>
    <title>Report</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
        .header { background: linear-gradient(135deg, #007bff, #0056b3); color: white; padding: 20px; margin: -40px -40px 30px -40px; }
        .metric { background: #f8f9fa; padding: 15px; margin: 10px 0; border-left: 4px solid #007bff; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; }
    </style>
</head>
<body>
    <div class="header">
        <h1>\$\{report.title\}</h1>
        <p>Generated on \$\{execution.startTime\}</p>
    </div>

    <div class="metric">
        <h3>Key Metrics</h3>
        <p><strong>Total Users:</strong> \$\{metrics.totalUsers\}</p>
        <p><strong>Active Sessions:</strong> \$\{metrics.activeSessions\}</p>
        <p><strong>Conversion Rate:</strong> \$\{metrics.conversionRate\}%</p>
    </div>

    <div class="metric">
        <h3>Summary</h3>
        <p>\$\{report.summary\}</p>
    </div>

    <div class="footer">
        <p>Report ID: \$\{execution.id\}</p>
    </div>
</body>
</html>`
			.replace(/\\\$\\\{/g, '{{')
			.replace(/\\\}/g, '}}'),
	},
];

const defaultConfig: PdfNodeConfig = {
	template: htmlTemplates[0].template,
	fileName: 'document-{{execution.id}}',
	access: 'private',
	format: 'A4',
	margin: {
		top: '1in',
		right: '1in',
		bottom: '1in',
		left: '1in',
	},
	timeout: 30000,
};

export function PdfConfig({
	nodeId,
	data,
	onChange,
	onValidationRequest,
	onClose,
}: PdfConfigProps) {
	const isDark = useIsDark();
	const { getFlattenedFields } = useWorkflowContext();
	const editorRef = useRef<any>(null);
	const [config, setConfig] = useState<PdfNodeConfig>(() => ({
		...defaultConfig,
		...data.config,
	}));

	const [isPreviewLoading, setIsPreviewLoading] = useState(false);
	const [showTokenPicker, setShowTokenPicker] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [templateValidation, setTemplateValidation] = useState<{
		valid: boolean;
		errors: string[];
		warnings: string[];
		stats?: { size: number; hasHtmlTag: boolean; hasBodyTag: boolean };
	} | null>(null);
	const [showAdvanced, setShowAdvanced] = useState(false);

	// Validation logic - memoize the config object to prevent re-renders
	const validationConfig = useMemo(
		() => ({
			template: config.template,
			fileName: config.fileName,
			access: config.access,
			format: config.format,
			timeout: config.timeout,
		}),
		[config.template, config.fileName, config.access, config.format, config.timeout]
	);

	const { validationResult, validateNode } = useNodeValidation(nodeId, validationConfig);

	// Update parent component when config changes
	useEffect(() => {
		onChange({
			...data,
			config,
		});
	}, [config]); // Remove data and onChange from dependencies to prevent infinite loops

	// Validate template when it changes - debounce to prevent excessive calls
	const debouncedValidateTemplate = useDebouncedCallback((template: string) => {
		if (template && template.length > 0) {
			validateTemplate(template);
		}
	}, 500);

	useEffect(() => {
		debouncedValidateTemplate(config.template);
	}, [config.template, debouncedValidateTemplate]);

	// Register validation function with parent - memoize to prevent re-renders
	const validationFunction = useCallback(async () => {
		const isValid = await validateNode();
		return isValid && (templateValidation?.valid ?? false);
	}, [validateNode, templateValidation]);

	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(validationFunction);
		}
	}, [onValidationRequest, validationFunction]);

	const validateTemplate = useCallback(async (template: string) => {
		try {
			const response = await fetch('/api/v1/composer/pdf/validate-template', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
				},
				body: JSON.stringify({ template }),
			});

			if (response.ok) {
				const result = await response.json();
				setTemplateValidation(result);
			} else if (response.status === 404) {
				// Backend not restarted yet - provide basic client-side validation
				setTemplateValidation({
					valid: true,
					errors: [],
					warnings: [
						'Server validation unavailable - restart backend to enable full validation',
					],
					stats: {
						size: template.length,
						hasHtmlTag: template.toLowerCase().includes('<html'),
						hasBodyTag: template.toLowerCase().includes('<body'),
					},
				});
			} else {
				setTemplateValidation({
					valid: false,
					errors: ['Failed to validate template'],
					warnings: [],
				});
			}
		} catch (error) {
			// Provide basic client-side validation as fallback
			setTemplateValidation({
				valid: true,
				errors: [],
				warnings: ['Network error - basic validation only'],
				stats: {
					size: template.length,
					hasHtmlTag: template.toLowerCase().includes('<html'),
					hasBodyTag: template.toLowerCase().includes('<body'),
				},
			});
		}
	}, []);

	const handlePreview = useCallback(async () => {
		if (!config.template) return;

		setIsPreviewLoading(true);
		try {
			// Get available fields for token resolution
			const availableFields = getFlattenedFields(nodeId);
			const mockData: Record<string, any> = {};

			// Create mock data from available fields
			availableFields.forEach(field => {
				if (field.type === 'string') {
					mockData[field.id] = `Sample ${field.label}`;
				} else if (field.type === 'number') {
					mockData[field.id] = 123;
				} else if (field.type === 'boolean') {
					mockData[field.id] = true;
				} else if (field.type === 'array') {
					mockData[field.id] = ['item1', 'item2'];
				} else {
					mockData[field.id] = `{${field.label} value}`;
				}
			});

			// Add execution mock data
			mockData.execution = {
				id: 'preview-execution',
				startTime: new Date().toISOString(),
				currentStep: 1,
				totalSteps: 1,
			};

			const response = await fetch('/api/v1/composer/pdf/preview', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
				},
				body: JSON.stringify({
					template: config.template,
					data: mockData,
					format: config.format,
					margin: config.margin,
					filename: config.fileName.replace(/\{\{[^}]+\}\}/g, 'preview'),
				}),
			});

			if (response.ok) {
				// Create blob and download
				const blob = await response.blob();
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = 'preview.pdf';
				a.click();
				window.URL.revokeObjectURL(url);
			} else if (response.status === 404) {
				alert(
					'PDF preview unavailable - please restart the backend server to enable PDF functionality'
				);
			} else {
				throw new Error('Preview generation failed');
			}
		} catch (error) {
			console.error('Preview error:', error);
			// TODO: Show error toast
		} finally {
			setIsPreviewLoading(false);
		}
	}, [config, nodeId, getFlattenedFields]);

	const updateConfig = useCallback((updates: Partial<PdfNodeConfig>) => {
		setConfig(prev => ({ ...prev, ...updates }));
	}, []);

	// Handle Esc key to exit fullscreen
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isFullscreen) {
				setIsFullscreen(false);
			}
		};

		if (isFullscreen) {
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [isFullscreen]);

	// Function to insert variable at cursor position in Monaco Editor
	const insertVariable = useCallback(
		(variable: string) => {
			if (editorRef.current) {
				const model = editorRef.current.getModel();
				const position = editorRef.current.getPosition();
				if (model && position) {
					const value = model.getValue();
					const offset = model.getOffsetAt(position);
					const tokenToInsert = `{{${variable}}}`;
					const newValue = value.slice(0, offset) + tokenToInsert + value.slice(offset);
					model.setValue(newValue);
					editorRef.current.setPosition({
						...position,
						column: position.column + tokenToInsert.length,
					});
					editorRef.current.focus();
					updateConfig({ template: newValue });
				}
			}
		},
		[updateConfig]
	);

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-3">
				<FileText className="w-5 h-5 text-blue-600" />
				<h3 className="text-lg font-semibold">PDF Generator Configuration</h3>
			</div>

			{/* Template Editor */}
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
						HTML Template
					</label>
					<div className="flex items-center gap-2">
						<select
							value=""
							onChange={e => {
								if (e.target.value) {
									const template = htmlTemplates.find(
										t => t.name === e.target.value
									);
									if (template) {
										updateConfig({ template: template.template });
									}
								}
							}}
							className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
						>
							<option value="">Load Template...</option>
							{htmlTemplates.map(template => (
								<option key={template.name} value={template.name}>
									{template.name}
								</option>
							))}
						</select>
						<button
							onClick={() => setIsFullscreen(true)}
							className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
							title="Open fullscreen editor"
						>
							<Maximize2 className="w-4 h-4" />
							Fullscreen
						</button>
						<button
							onClick={() => setShowTokenPicker(!showTokenPicker)}
							className="flex items-center gap-2 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
						>
							<Plus className="w-4 h-4" />
							Insert Variable
							<ChevronDown
								className={`w-4 h-4 transition-transform ${
									showTokenPicker ? 'rotate-180' : ''
								}`}
							/>
						</button>
						<button
							onClick={handlePreview}
							disabled={isPreviewLoading || !config.template}
							className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isPreviewLoading ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<Eye className="w-4 h-4" />
							)}
							Preview PDF
						</button>
					</div>
				</div>

				{/* Variable Picker Dropdown */}
				{showTokenPicker && (
					<div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
						<div className="flex items-center gap-2 mb-3">
							<Plus className="w-4 h-4 text-green-600" />
							<h4 className="text-sm font-medium text-green-900 dark:text-green-100">
								Available Variables
							</h4>
						</div>
						<p className="text-xs text-green-700 dark:text-green-300 mb-3">
							Click any variable to insert it at the cursor position in your HTML
							template
						</p>

						{/* Common execution variables */}
						<div className="mb-4">
							<h5 className="text-xs font-medium text-green-800 dark:text-green-200 mb-2">
								Execution Data:
							</h5>
							<div className="flex flex-wrap gap-2">
								{[
									{ id: 'execution.id', label: 'Execution ID' },
									{ id: 'execution.startTime', label: 'Start Time' },
									{ id: 'execution.currentStep', label: 'Current Step' },
								].map(variable => (
									<button
										key={variable.id}
										onClick={() => {
											insertVariable(variable.id);
											setShowTokenPicker(false);
										}}
										className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-150 dark:hover:bg-blue-900/50 transition-colors"
									>
										<div className="font-mono">{`{{${variable.id}}}`}</div>
									</button>
								))}
							</div>
						</div>

						{/* Upstream node variables */}
						{getFlattenedFields(nodeId).length > 0 && (
							<div>
								<h5 className="text-xs font-medium text-green-800 dark:text-green-200 mb-2">
									Upstream Data:
								</h5>
								<div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
									{getFlattenedFields(nodeId).map(field => (
										<button
											key={field.id}
											onClick={() => {
												insertVariable(field.id);
												setShowTokenPicker(false);
											}}
											className="px-3 py-2 text-xs bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 rounded hover:bg-green-50 dark:hover:bg-green-900/40 transition-colors"
											title={`Insert {{${field.id}}} - ${
												field.description || field.label
											}`}
										>
											<div className="font-medium">{field.label}</div>
											<div className="font-mono text-green-600 dark:text-green-400">
												{`{{${field.id}}}`}
											</div>
										</button>
									))}
								</div>
							</div>
						)}

						{getFlattenedFields(nodeId).length === 0 && (
							<p className="text-xs text-green-600 dark:text-green-400 italic">
								No upstream variables available. Add nodes before this one to access
								their data.
							</p>
						)}
					</div>
				)}

				<div className="border rounded-lg overflow-hidden">
					<Editor
						height="300px"
						defaultLanguage="html"
						theme={isDark ? 'vs-dark' : 'light'}
						value={config.template}
						onChange={value => updateConfig({ template: value || '' })}
						onMount={editor => {
							editorRef.current = editor;
						}}
						options={{
							minimap: { enabled: false },
							wordWrap: 'on',
							lineNumbers: 'on',
							folding: true,
							bracketMatching: 'always',
							automaticLayout: true,
						}}
					/>
				</div>

				{/* Template Validation Results */}
				{templateValidation && (
					<div className="space-y-2">
						{templateValidation.errors.length > 0 && (
							<div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
								<AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
								<div>
									<p className="text-sm font-medium text-red-800 dark:text-red-200">
										Template Issues:
									</p>
									<ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside">
										{templateValidation.errors.map((error, index) => (
											<li key={index}>{error}</li>
										))}
									</ul>
								</div>
							</div>
						)}

						{templateValidation.warnings.length > 0 && (
							<div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
								<AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
								<div>
									<p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
										Template Warnings:
									</p>
									<ul className="text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
										{templateValidation.warnings.map((warning, index) => (
											<li key={index}>{warning}</li>
										))}
									</ul>
								</div>
							</div>
						)}

						{templateValidation.stats && (
							<div className="text-xs text-gray-500 dark:text-gray-400">
								Template size: {templateValidation.stats.size} bytes
								{templateValidation.stats.hasHtmlTag ? '' : ' • Missing <html> tag'}
								{templateValidation.stats.hasBodyTag ? '' : ' • Missing <body> tag'}
							</div>
						)}
					</div>
				)}
			</div>

			{/* File Configuration */}
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						File Name
					</label>
					<input
						type="text"
						value={config.fileName}
						onChange={e => updateConfig({ fileName: e.target.value })}
						placeholder="document-{{execution.id}}"
						className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
						Use tokens like {`{{execution.id}}`} for dynamic filenames. Extension (.pdf)
						is added automatically.
					</p>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						Access Level
					</label>
					<div className="flex gap-4">
						<label className="flex items-center gap-2">
							<input
								type="radio"
								value="private"
								checked={config.access === 'private'}
								onChange={() => updateConfig({ access: 'private' })}
								className="text-blue-600"
							/>
							<Lock className="w-4 h-4" />
							<span className="text-sm">Private (Signed URLs)</span>
						</label>
						<label className="flex items-center gap-2">
							<input
								type="radio"
								value="public"
								checked={config.access === 'public'}
								onChange={() => updateConfig({ access: 'public' })}
								className="text-blue-600"
							/>
							<Unlock className="w-4 h-4" />
							<span className="text-sm">Public (Direct URLs)</span>
						</label>
					</div>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
						Private files use secure, time-limited URLs. Public files are directly
						accessible.
					</p>
				</div>
			</div>

			{/* Advanced Settings */}
			<div>
				<button
					onClick={() => setShowAdvanced(!showAdvanced)}
					className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
				>
					<Settings className="w-4 h-4" />
					Advanced Settings
				</button>

				{showAdvanced && (
					<div className="mt-4 space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Page Format
								</label>
								<select
									value={config.format}
									onChange={e => updateConfig({ format: e.target.value as any })}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
								>
									<option value="A4">A4</option>
									<option value="Letter">Letter</option>
									<option value="Legal">Legal</option>
								</select>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Timeout (seconds)
								</label>
								<input
									type="number"
									min="5"
									max="60"
									value={config.timeout ? config.timeout / 1000 : 30}
									onChange={e =>
										updateConfig({ timeout: parseInt(e.target.value) * 1000 })
									}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
								/>
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Page Margins
							</label>
							<div className="grid grid-cols-4 gap-2">
								{(['top', 'right', 'bottom', 'left'] as const).map(side => (
									<div key={side}>
										<label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">
											{side}
										</label>
										<input
											type="text"
											value={config.margin?.[side] || '1in'}
											onChange={e =>
												updateConfig({
													margin: {
														...config.margin,
														[side]: e.target.value,
													},
												})
											}
											placeholder="1in"
											className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
										/>
									</div>
								))}
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								Use CSS units like "1in", "2.5cm", "20px"
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Validation Results */}
			{validationResult && (
				<ValidationResultDisplay
					nodeId={nodeId}
					validationResult={validationResult}
					showSuccessState={true}
				/>
			)}

			{/* Help Text */}
			<div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
				<HelpCircle className="w-4 h-4 text-blue-600 mt-0.5" />
				<div className="text-sm text-blue-800 dark:text-blue-200">
					<p className="font-medium mb-1">PDF Generation Tips:</p>
					<ul className="list-disc list-inside space-y-1">
						<li>Use standard HTML and CSS for best results</li>
						<li>
							Include tokens like {`{{data.fieldName}}`} to insert dynamic content
						</li>
						<li>Private PDFs use secure URLs that expire after 15 minutes</li>
						<li>Test your template with the Preview button before saving</li>
					</ul>
				</div>
			</div>

			{/* Fullscreen Editor Modal */}
			{isFullscreen && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
					<div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-[95vw] h-[90vh] flex flex-col">
						{/* Fullscreen Header */}
						<div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
							<div className="flex items-center gap-3">
								<FileText className="w-5 h-5 text-blue-600" />
								<h3 className="text-lg font-semibold">HTML Template Editor</h3>
							</div>
							<div className="flex items-center gap-2">
								<button
									onClick={() => setShowTokenPicker(!showTokenPicker)}
									className="flex items-center gap-2 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
								>
									<Plus className="w-4 h-4" />
									Insert Variable
									<ChevronDown
										className={`w-4 h-4 transition-transform ${
											showTokenPicker ? 'rotate-180' : ''
										}`}
									/>
								</button>
								<button
									onClick={handlePreview}
									disabled={isPreviewLoading || !config.template}
									className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isPreviewLoading ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										<Eye className="w-4 h-4" />
									)}
									Preview PDF
								</button>
								<button
									onClick={() => setIsFullscreen(false)}
									className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
								>
									<Minimize2 className="w-4 h-4" />
									Exit Fullscreen
								</button>
							</div>
						</div>

						{/* Variable Picker in Fullscreen */}
						{showTokenPicker && (
							<div className="p-4 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
								<div className="flex items-center gap-2 mb-3">
									<Plus className="w-4 h-4 text-green-600" />
									<h4 className="text-sm font-medium text-green-900 dark:text-green-100">
										Available Variables
									</h4>
								</div>
								<p className="text-xs text-green-700 dark:text-green-300 mb-3">
									Click any variable to insert it at the cursor position in your
									HTML template
								</p>

								{/* Common execution variables */}
								<div className="mb-4">
									<h5 className="text-xs font-medium text-green-800 dark:text-green-200 mb-2">
										Execution Data:
									</h5>
									<div className="flex flex-wrap gap-2">
										{[
											{ id: 'execution.id', label: 'Execution ID' },
											{ id: 'execution.startTime', label: 'Start Time' },
											{ id: 'execution.currentStep', label: 'Current Step' },
										].map(variable => (
											<button
												key={variable.id}
												onClick={() => {
													insertVariable(variable.id);
													setShowTokenPicker(false);
												}}
												className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-150 dark:hover:bg-blue-900/50 transition-colors"
											>
												<div className="font-mono">{`{{${variable.id}}}`}</div>
											</button>
										))}
									</div>
								</div>

								{/* Upstream node variables */}
								{getFlattenedFields(nodeId).length > 0 && (
									<div>
										<h5 className="text-xs font-medium text-green-800 dark:text-green-200 mb-2">
											Upstream Data:
										</h5>
										<div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
											{getFlattenedFields(nodeId).map(field => (
												<button
													key={field.id}
													onClick={() => {
														insertVariable(field.id);
														setShowTokenPicker(false);
													}}
													className="px-3 py-2 text-xs bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 rounded hover:bg-green-50 dark:hover:bg-green-900/40 transition-colors"
													title={`Insert {{${field.id}}} - ${
														field.description || field.label
													}`}
												>
													<div className="font-medium">{field.label}</div>
													<div className="font-mono text-green-600 dark:text-green-400">
														{`{{${field.id}}}`}
													</div>
												</button>
											))}
										</div>
									</div>
								)}

								{getFlattenedFields(nodeId).length === 0 && (
									<p className="text-xs text-green-600 dark:text-green-400 italic">
										No upstream variables available. Add nodes before this one
										to access their data.
									</p>
								)}
							</div>
						)}

						{/* Fullscreen Editor */}
						<div className="flex-1 overflow-hidden">
							<Editor
								height="100%"
								defaultLanguage="html"
								theme={isDark ? 'vs-dark' : 'light'}
								value={config.template}
								onChange={value => updateConfig({ template: value || '' })}
								onMount={editor => {
									editorRef.current = editor;
								}}
								options={{
									minimap: { enabled: true },
									wordWrap: 'on',
									lineNumbers: 'on',
									folding: true,
									bracketMatching: 'always',
									automaticLayout: true,
									fontSize: 14,
									scrollBeyondLastLine: false,
								}}
							/>
						</div>

						{/* Fullscreen Footer */}
						<div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
							<div className="flex items-center justify-between">
								<div className="text-sm text-gray-600 dark:text-gray-400">
									Template size: {config.template.length} characters
								</div>
								<div className="flex items-center gap-2">
									<kbd className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded">
										Esc
									</kbd>
									<span className="text-xs text-gray-500">
										to exit fullscreen
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
